import { createFileRoute, notFound } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';

import { BlogPost } from '#features/blog/components/BlogPost';
import { blogPostBySlugQueryOptions } from '#features/blog/model/blog';
import { envConfig } from '#constants/envConfig';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { Link } from '#features/routing';
import { seo } from '#utils/seo';

export const Route = createFileRoute('/{-$locale}/_layout/blog/$slug')({
  loader: async ({ params: { slug }, context }) => {
    try {
      const data = await context.queryClient.ensureQueryData(blogPostBySlugQueryOptions(slug));

      if (!data) {
        throw notFound();
      }

      return {
        title: data.ogTitle,
        description: data.ogDescription,
        keywords: data.keywords,
        image: data.previewImage,
        date: data.date,
        updatedAt: data.updatedAt,
        slug,
      };
    } catch (error) {
      console.error(error);
      throw notFound();
    }
  },
  notFoundComponent: ArticleNotFound,
  head: ({ loaderData }) => {
    if (!loaderData) return {};

    const url = `${envConfig.HOST}/blog/${loaderData.slug}`;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: loaderData.title,
      description: loaderData.description,
      ...(loaderData.image && { image: loaderData.image }),
      ...(loaderData.date && { datePublished: loaderData.date }),
      // dateModified — из updatedAt: правка статьи должна быть видна поисковику как правка,
      // а не выглядеть публикацией, которую с тех пор не трогали.
      ...((loaderData.updatedAt ?? loaderData.date) && {
        dateModified: loaderData.updatedAt ?? loaderData.date,
      }),
      url,
      author: {
        '@type': 'Organization',
        name: 'Teamplay',
        url: envConfig.HOST,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Teamplay',
        url: envConfig.HOST,
        ...(loaderData.image && {
          logo: {
            '@type': 'ImageObject',
            url: loaderData.image,
          },
        }),
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    };

    return {
      meta: seo({
        ...loaderData,
        url,
        type: 'article',
        publishedTime: loaderData.date,
        modifiedTime: loaderData.updatedAt ?? loaderData.date,
      }),
      links: [{ rel: 'canonical', href: url }],
      scripts: [
        {
          id: 'blog-post-jsonld',
          type: 'application/ld+json',
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: BlogPost,
});

function ArticleNotFound() {
  const { t } = useLingui();

  return (
    <ContentNotFound
      title={t`notFound.article.title`}
      description={t`notFound.article.description`}
      action={<Link to="/{-$locale}/blog">{t`notFound.allArticles`}</Link>}
    />
  );
}
